import { trackDemoRequested } from '@features/demo/analytics';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { setTokenGetter, useAuthContext } from './AuthContext';
import { Outlet } from 'react-router-dom';
import Loader from '@shared/components/Loader';
import DemoRegistrationDialog from '@features/demo/components/DemoRegistrationDialog';
import { useDemoMode } from '@features/demo/store';
import { useIntl } from 'react-intl';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

const AuthSync = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout } = useAuth0();
  const { ready, updateAuthState, isDemo } = useAuthContext();
  const { identifyUser, resetUser } = useAnalytics();
  const intl = useIntl();
  const { isDemoParam, showDemoDialog, setShowDemoDialog } = useDemoMode();
  useEffect(() => {
    setTokenGetter(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isDemoParam || isDemo) {
      trackDemoRequested();
      return;
    }

    if (isLoading) {
      return;
    }

    const sync = async () => {
      if (!isAuthenticated) {
        Object.keys(sessionStorage)
          .filter(key => key.startsWith('returnTrialAttempt:'))
          .forEach(key => sessionStorage.removeItem(key));
        resetUser();
        updateAuthState({
          isAuthenticated: false,
          user: undefined,
          isDemo: false,
          token: undefined,
          ready: true,
        });
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        if (user?.sub) {
          identifyUser(user.sub, {
            auth_provider: 'auth0',
            user_email_domain: user.email?.split('@')[1],
          });
        }

        updateAuthState({
          isAuthenticated: true,
          user,
          isDemo: false,
          token,
          ready: true,
        });
      } catch {
        updateAuthState({
          isAuthenticated: false,
          user: undefined,
          isDemo: false,
          token: undefined,
          ready: true,
        });
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        logout({
          logoutParams: {
            returnTo: `${window.location.origin}/hairdresser`,
          },
        });
      }
    };

    sync();
  }, [
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    logout,
    ready,
    showDemoDialog,
    isDemoParam,
    isDemo,
    identifyUser,
    updateAuthState,
  ]);

  const handleDemoRegistrationSuccess = (email: string) => {
    localStorage.setItem('demoUserEmail', email);
    updateAuthState({
      isAuthenticated: false,
      user: undefined,
      isDemo: true,
      token: undefined,
      ready: true,
    });
    setShowDemoDialog(false);
  };

  if (showDemoDialog) {
    return (
      <DemoRegistrationDialog open={showDemoDialog} onSuccess={handleDemoRegistrationSuccess} />
    );
  }

  if (isDemo && ready) {
    return <Outlet />;
  }

  if (!ready || isLoading) {
    return (
      <Loader
        title={intl.formatMessage({
          id: 'authSync.loading',
          defaultMessage: 'Načítání...',
        })}
      />
    );
  }

  return <Outlet />;
};

export default AuthSync;
